<template>
  <component
    :is="element"
    data-test="button"
    :data-qa="`button-${styleType}`"
    :class="['base-button', styleType, { [styleType + '_loading']: isLoading }]"
    :disabled="isElementButton ? disabled : null"
    :type="isElementButton ? type : null"
    @click="onClick"
  >
    <img
      v-if="isLoading && isLoadingViewExists"
      :src="spinner"
      class="base-button__spinner"
      alt="loading"
    />
    <template v-else>
      <span class="base-button__content">
        <slot name="icon-left" />
        <slot />
        <slot name="icon-right" />
      </span>
    </template>
  </component>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import spinner from '@/assets/icons/spinner-navy.svg';

  interface Props {
    type?: 'button' | 'submit' | 'reset'
    styleType?:
      | 'primary'
      | 'primary-danger'
      | 'secondary'
      | 'secondary-danger'
      | 'tertiary'
      | 'tertiary-danger'
      | 'empty'
      | 'icon-only'
      | 'link'
    isLoading?: boolean
    disabled?: boolean
    element?: 'button' | 'span' | 'div'
  }

  const emits = defineEmits<{
    (e: 'click', v: Event): void
  }>();

  const props = withDefaults(defineProps<Props>(), {
    type: 'button',
    styleType: 'primary',
    isLoading: false,
    disabled: false,
    element: 'button',
  });

  const isLoadingViewExists = computed(() => props.styleType !== 'tertiary');
  const isElementButton = computed(() => props.element === 'button');

  const onClick = (event: Event) => {
    if (props.disabled || props.isLoading) event.preventDefault();
    else emits('click', event);
  };
</script>

<style scoped lang="scss">
  .base-button {
    $p: &;

    display: flex;
    justify-content: center;
    padding: 12px 16px;
    transition: all 0.1s ease-in-out;

    &__content {
      display: flex;
      gap: 8px;
      align-items: center;
      font-size: remc(16px);
      font-weight: 500;
      line-height: remc(24px);
      text-align: center;
    }

    &__spinner {
      width: 24px;
      height: 24px;
      color: $light-navy-80;
    }

    &.primary-danger,
    &.primary {
      width: 100%;
      color: $brand-primary-100-navy;
      background: $brand-primary-100-teal;
      border: 1px solid $brand-primary-100-teal;
      border-radius: 4px;

      #{$p}__content {
        font-weight: 600;
      }

      &-danger {
        color: $brand-primary-100-white;
        background: $brand-secondary-100-red;
        border: 1px solid $brand-secondary-100-red;
      }

      &:hover {
        background: $brand-primary-100-teal;
        border: 1px solid $brand-primary-100-teal;
      }

      &:disabled {
        color: $brand-primary-100-navy;
        cursor: not-allowed;
        background: $brand-primary-100-teal;
        border: 1px solid $brand-primary-100-teal;
        opacity: 0.4;
      }

      &_loading {
        color: $brand-primary-100-navy;
        background: $brand-primary-100-teal;
        border: 1px solid $brand-primary-100-teal;

        &:hover {
          background: $brand-primary-100-teal;
          border: 1px solid $brand-primary-100-teal;
        }

        &:disabled {
          color: $brand-primary-100-navy;
          cursor: not-allowed;
          background: $brand-primary-100-teal;
          border: 1px solid $brand-primary-100-teal;
          opacity: 1;
        }
      }
    }

    &.secondary-danger,
    &.secondary {
      width: 100%;
      color: $brand-primary-100-navy;
      background: $brand-primary-100-white;
      border: 1px solid $brand-primary-100-teal;
      border-radius: 4px;

      &-danger {
        color: $brand-secondary-100-red;
        background: $brand-primary-100-white;
        border: 1px solid $brand-secondary-100-red;
      }

      &:hover {
        color: $brand-primary-100-navy;
        background: $brand-primary-100-white;
        border: 1px solid $brand-primary-100-teal;
        box-shadow: inset 0 0 0 1 $brand-primary-100-teal;
      }

      &:disabled {
        color: $brand-primary-100-navy;
        cursor: not-allowed;
        background: $brand-primary-100-white;
        border: 1px solid $brand-primary-100-teal;
        opacity: 0.4;

        &:hover {
          outline: none;
        }
      }

      &_loading {
        color: $brand-primary-100-navy;
        background: $brand-primary-100-white;
        border: 1px solid $brand-primary-100-teal;
        opacity: 1;

        &:hover {
          outline: none;
        }

        &:disabled {
          color: $brand-primary-100-navy;
          cursor: not-allowed;
          background: $brand-primary-100-white;
          border: 1px solid $brand-primary-100-teal;
          opacity: 1;

          &:hover {
            box-shadow: inset 0 0 0 1px $brand-primary-100-teal;
          }
        }
      }
    }

    &.tertiary-danger,
    &.tertiary {
      display: inline-block;
      max-width: max-content;
      padding: 0;
      color: $text-100-link-blue;
      text-decoration: underline;
      background: transparent;

      &-danger {
        color: $brand-secondary-100-red;
      }

      &:hover {
        color: $brand-secondary-100-light-navy;
      }

      &:disabled {
        color: $light-navy-80;
        cursor: not-allowed;
      }
    }

    &.icon-only {
      min-width: auto;
      padding: 0;
      background: none;
    }

    &.empty {
      display: inline-block;
      min-width: auto;
      padding: 0;
      background: none;
      border-radius: 0;

      &:hover,
      &:active {
        background: none;
      }
    }

    &.link {
      display: flex;
      align-items: center;
      min-width: auto;
      padding: 0;
      color: $link-text;
      background: none;
      border-radius: 0;

      &:active {
        background: none;
      }

      &:hover {
        color: $sky-blue-80;
        transition: color 0.2s;
      }
    }
  }
</style>
